import React, { useEffect, useMemo, forwardRef, useCallback } from 'react';
import { Button, Form } from 'antd'
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes, generateFormFields } from 'utils/formUtils';
import GenericMappingForm from '../GenericMappingForm';
import _ from 'lodash';
import { useRef } from 'react';
import { useState } from 'react';

const initialFormValues = {
    product: undefined,
    subProduct: undefined,
    environment: undefined,
    projectList: [{ repositoryIds: undefined }]
};

const CycodeMappingFormV2 = forwardRef(({ disableProjectFields = false, ...props }, ref) => {
    const projectsRef = useRef({});
    const repoRef = useRef({});
    const projectApiRef = useRef([]);
    const [form] = Form.useForm();
    const cancelRef = useRef([]);
    const [initialValues] = useState(() => {
        if (props.record) {
            return [];
        }
        return [{ ...initialFormValues }];
    });

    const [mappedReposProjectList] = useState(() => {
        // this state contains the already mapped repos/projects
        // This is stored because the repositoryIds and project dropdowns are paginated
        // and if the option selected is not on 2st page, then the ID is displayed as this is how select works
        if (props.record) {
            const { repositoryIds, repositoryNames, projectId, projectName } = props.record;
            const repositoryIdsArray = repositoryIds.split(',');
            const repositoryNamesArray = repositoryNames.split(',');
            const projectIdArray = projectId?.split(';');
            const projectNameArray = projectName?.split(';');

            const repos = [], projects = {}


            _.forEach(repositoryIdsArray, (repoId, index) => {

                repos.push({ id: repoId, name: repositoryNamesArray[index] });
                repoRef.current = { ...repoRef.current, [repoId]: repositoryNamesArray[index] }

                if (projectIdArray && projectIdArray[index]) {
                    projects[repoId] = [];
                    const _projectNameArray = projectNameArray[index].split(',')
                    _.forEach(_.split(projectIdArray[index], ','), (proID, i) => {
                        projects[repoId].push({ id: proID, name: _projectNameArray[i] })
                    })
                }
            });
            return {
                repos: _.uniqBy(repos, "id"),
                projects
            }
        }
        return { repos: [], projects: {} }
    })

    useEffect(() => {
        // cancelling all project APIs on component unmount
        const cancels = cancelRef.current;
        return () => _.forEach(cancels, cancel => cancel());
    }, []);

    const fetchProject = useCallback((params) => {
        const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, ...params });
        return {
            promise: promise.then(response => {
                // merging the already mapped projects to the fetched list so that if the selected value is not in the option list
                // then id should not be displayed
                if (mappedReposProjectList.projects[params.repositoryIds] && !_.isEmpty(response.projects)) {
                    response.projects = [...response.projects, mappedReposProjectList.projects[params.repositoryIds]];
                }


                _.forEach(response.projects, p => {
                    projectsRef.current[p.id] = p.name;
                });

                return response;
            }),
            cancel
        }
    }, [props.loginConfigId, props.toolName, mappedReposProjectList]);

    const fetchRepos = useCallback(() => {
        let { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId });
        promise = promise.then(response => {
            // merging the already mapped projects to the fetched list so that if the selected value is not in the option list
            // then id should not be displayed
            if (mappedReposProjectList.repos.length > 0 && !_.isEmpty(response.projects)) {
                response.projects = [...response.projects, ...mappedReposProjectList.repos];
            }

            _.forEach(response.projects, p => {
                repoRef.current[p.id] = p.name;
            });

            return response.projects;
        });
        return {
            promise,
            cancel
        }
    }, [props.loginConfigId, props.toolName, mappedReposProjectList]);

    const getExtraFieldsData = useCallback((config) => {
        const { projectList } = config;
        const data = _.map(projectList, ({ repositoryIds, project }) => {

            const repositoryNames = repoRef.current[repositoryIds];
            const projectName = _.isArray(project) ? _.map(project, v => projectsRef.current[v]).join(',') : repoRef.current[project];
            return { repositoryIds, repositoryNames, project: _.isArray(project) ? project.join(',') : project, projectName };
        });
        return ({
            repositoryIds: _.map(data, 'repositoryIds').join(','),
            repositoryNames: _.map(data, 'repositoryNames').join(','),
            projectId: _.map(data, 'project').join(';'),
            projectName: _.map(data, 'projectName').join(';'),
            //send the property received in config
            ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
        })
    }, []);

    const parseExtraFieldsDataFromResponse = useCallback((config) => {
        const { projectId, repositoryIds } = config;

        const repository = repositoryIds.split(',')
        const projects = projectId?.split(';')
        const data = _.map(repository, (repos, index) => {
            return { repositoryIds: repos, project: _.isNil(projects) || _.isEmpty(projects[index]) ? null :  projects[index].split(',')}
        });

        return ({
            projectList: data
        });
    }, []);

    const onValuesChange = useCallback((values, ...args) => {
        if (values.configurations) {
            let isProjectChanged = false;
            const formValues = form.getFieldsValue();
            _.forEach(values.configurations, (configs, index) => {

                // If repositoryIds list is greater than 1, means that a new blank row is added
                // no need to clear the project fields
                if (!configs || !configs.projectList || configs.projectList.length > 1) {
                    return;
                }

                // since this is a list field, onchange will trigger only for the changed row.
                // if change is done at 2nd row, data received in this callback will only contain 2nd row data
                // configs.projectList is given as string when we search in the field
                if (!configs || _.isString(configs.projectList)) {
                    return;
                }
                _.forEach(configs.projectList, (object, i) => {

                    // since this is a list field, onchange will trigger only for the changed row.
                    // if change is done at 2nd row, data received in this callback will only contain 2nd row data
                    // Also checking if the object contains `repositoryIds` and nothing else 
                    // because we need to reset the project field only if the repositoryIds is changed
                    // and this change is also triggered when rows are added or deleted, so we need to ignore it
                    if (object && object.repositoryIds && Object.keys(object).length === 1) {
                        isProjectChanged = true;
                        formValues.configurations[index].projectList[i].project = undefined;
                    }
                })
            });
            if (isProjectChanged) {
                form.setFieldsValue(formValues);
            }
        }
    }, [form]);

    const projectListRender = useCallback((listField) => {

        // This is a hack, we are storing the api method for project in a ref
        // as it is dependant on `listField` param of this method
        // that is why we cannot move it to `useCallback`
        // For a `listField.name` we are defining the method only once and using the method if it alreadr exists in the ref
        // Reason for doing this is that the method reference will always change and will trigger API on every render
        // It also causes a visible bug on UI as when the API triggers, the selected value disappears as we don't show value when data is loading
        let projectAPI = projectApiRef.current[listField.name];
        if (!projectAPI) {
            projectAPI = projectApiRef.current[listField.name] = ({ listFieldName: nestedListFieldName, ...params }) => {
                const allConfigs = form.getFieldsValue().configurations;
                const repositoryIds = allConfigs[listField.name]?.projectList[nestedListFieldName]?.repositoryIds;
                if (!repositoryIds) {
                    return { promise: Promise.resolve([]), cancel: _.noop };
                }
                return fetchProject({ ...params, type: 'project', name: repositoryIds });
            }
        }
        // 

        const __fields = [{
            name: 'repositoryIds',
            type: FieldTypes.SELECT,
            disabled: disableProjectFields,
            colSpan: 9,
            // loading: _.isUndefined(repos),
            props: {
                showValueOnload: false,
                autoFocus: !disableProjectFields,
                optionsMutator: (repos) => {
                    const selected = _.compact(_.map(form.getFieldsValue().configurations?.[listField.name]?.projectList, p => p?.repositoryIds));
                    return _.map(repos, p => ({ ...p, disabled: _.includes(selected, p.id) }));
                },
                api: fetchRepos
            },
            rules: [{ required: true, message: 'Cycode Repository is required' }]
        }, {
            name: 'project',
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            disabled: disableProjectFields,
            colSpan: 9,
            props: {
                api: projectAPI,
                mode: "multiple",
                setExtraProps: (field, nestedListField) => {
                    const allConfigs = form.getFieldsValue().configurations;
                    const repositoryIds = allConfigs[listField.name]?.projectList[nestedListField.name]?.repositoryIds;
                    return {
                        showValueOnload: false,
                        resetOptionsProp: repositoryIds
                    }
                }
            },
        }, {
            name: "add",
            colSpan: 2,
            render: ({ remove, name, fields }) => {
                return <Button disabled={fields.length === 1} type='dashed' onClick={() => { remove(name) }}>-</Button>;
            }
        }]

        return (
            <div className='m-b-sm'>
                <Form.List name={[listField.name, "projectList"]}>
                    {(fields, { add, remove }) => {
                        return (
                            <div>
                                {_.map(fields, field => {
                                    return generateFormFields(__fields, 9, { ...field, add, remove, fields }, field.name);
                                })}
                                <Button size='small' type='dashed' onClick={() => add({ repositoryIds: undefined })} disabled={disableProjectFields}>+ Add Project</Button>
                            </div>
                        )
                    }}
                </Form.List>
            </div>
        )
    }, [disableProjectFields, form, fetchRepos, fetchProject]);

    const _fields = useMemo(() => [{
        name: "label",
        type: FieldTypes.LABEL,
        value: <b className='required-mark'>Repository</b>,
        style: { marginBottom: -24 },
        colSpan: 9
    }, {
        name: "label",
        type: FieldTypes.LABEL,
        value: <b>Project</b>,
        style: { marginBottom: -24 },
        colSpan: 9
    }, {
        name: (listField) => [listField.name, "projectList"],
        render: projectListRender
    },
    {
        type: FieldTypes.DIVIDER
    }], [projectListRender]);

    return (
        <GenericMappingForm
            form={form}
            ref={ref}
            getExtraFieldsData={getExtraFieldsData}
            parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
            extraFields={_fields}
            formDefaultValues={initialValues}
            newRowDefaultValue={initialValues[0]}
            {...props}
            onValuesChange={onValuesChange}
        />
    )
})

export default CycodeMappingFormV2;
